import { useState, useEffect, useCallback } from 'react';
import { kanbanService, KanbanBoard, KanbanColumnWithItems, CreateColumnRequest, UpdateColumnRequest, MoveItemRequest } from '@/services/kanbanService';
import { useSharedCart } from '@/components/shared-cart/SharedCartProvider';

export interface UseKanbanReturn {
  board: KanbanBoard | null;
  columns: KanbanColumnWithItems[];
  loading: boolean;
  error: string | null;
  refreshBoard: () => Promise<void>;
  createColumn: (column: CreateColumnRequest) => Promise<void>;
  updateColumn: (columnId: string, column: UpdateColumnRequest) => Promise<void>;
  deleteColumn: (columnId: string) => Promise<void>;
  moveItem: (moveRequest: MoveItemRequest) => Promise<void>;
  updateItemPosition: (placementId: string, position: number) => Promise<void>;
  removeItemFromBoard: (placementId: string) => Promise<void>;
}

export function useKanban(sessionId: string): UseKanbanReturn {
  const [board, setBoard] = useState<KanbanBoard | null>(null);
  const [columns, setColumns] = useState<KanbanColumnWithItems[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { sendMessage, isConnected, addMessageListener } = useSharedCart();

  const refreshBoard = useCallback(async () => {
    if (!sessionId) return;

    setLoading(true);
    setError(null);

    try {
      const boardData = await kanbanService.getKanbanBoard(sessionId);
      setBoard(boardData);
      setColumns(boardData.columns);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load kanban board');
      console.error('Failed to load kanban board:', err);
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  const createColumn = useCallback(async (column: CreateColumnRequest) => {
    if (!sessionId) return;

    try {
      const newColumn = await kanbanService.createColumn(sessionId, column);
      setColumns(prev => [...prev, { ...newColumn, items: [] }].sort((a, b) => a.position - b.position));
      
      // Send real-time update
      if (isConnected) {
        sendMessage('kanban_column_created', {
          column: newColumn,
          cartId: sessionId,
        });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create column');
      throw err;
    }
  }, [sessionId, isConnected, sendMessage]);

  const updateColumn = useCallback(async (columnId: string, column: UpdateColumnRequest) => {
    if (!sessionId) return;

    try {
      const updatedColumn = await kanbanService.updateColumn(sessionId, columnId, column);
      setColumns(prev => 
        prev.map(col => col.id === columnId ? { ...updatedColumn, items: col.items } : col)
          .sort((a, b) => a.position - b.position)
      );

      // Send real-time update
      if (isConnected) {
        sendMessage('kanban_column_updated', {
          column: updatedColumn,
          cartId: sessionId,
        });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update column');
      throw err;
    }
  }, [sessionId, isConnected, sendMessage]);

  const deleteColumn = useCallback(async (columnId: string) => {
    if (!sessionId) return;

    try {
      await kanbanService.deleteColumn(sessionId, columnId);
      setColumns(prev => prev.filter(col => col.id !== columnId));

      // Send real-time update
      if (isConnected) {
        sendMessage('kanban_column_deleted', {
          columnId,
          cartId: sessionId,
        });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete column');
      throw err;
    }
  }, [sessionId, isConnected, sendMessage]);

  const moveItem = useCallback(async (moveRequest: MoveItemRequest) => {
    if (!sessionId) return;

    try {
      const placement = await kanbanService.moveItemToColumn(sessionId, moveRequest);
      
      // Refresh board to get updated state
      await refreshBoard();

      // Send real-time update
      if (isConnected) {
        sendMessage('kanban_item_moved', {
          placement,
          itemId: moveRequest.itemId,
          columnId: moveRequest.columnId,
          position: moveRequest.position,
          cartId: sessionId,
        });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to move item');
      throw err;
    }
  }, [sessionId, isConnected, sendMessage, refreshBoard]);

  const updateItemPosition = useCallback(async (placementId: string, position: number) => {
    if (!sessionId) return;

    try {
      await kanbanService.updateItemPosition(sessionId, placementId, position);
      
      // Refresh board to get updated state
      await refreshBoard();

      // Send real-time update
      if (isConnected) {
        sendMessage('kanban_item_position_updated', {
          placementId,
          position,
          cartId: sessionId,
        });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update item position');
      throw err;
    }
  }, [sessionId, isConnected, sendMessage, refreshBoard]);

  const removeItemFromBoard = useCallback(async (placementId: string) => {
    if (!sessionId) return;

    try {
      await kanbanService.removeItemFromBoard(sessionId, placementId);
      
      // Refresh board to get updated state
      await refreshBoard();

      // Send real-time update
      if (isConnected) {
        sendMessage('kanban_item_removed', {
          placementId,
          cartId: sessionId,
        });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove item from board');
      throw err;
    }
  }, [sessionId, isConnected, sendMessage, refreshBoard]);

  // Load board on mount
  useEffect(() => {
    refreshBoard();
  }, [refreshBoard]);

  // Listen for real-time updates from other participants via the shared
  // WebSocket connection
  useEffect(() => {
    const unsubscribe = addMessageListener((message) => {
      switch (message.type) {
        case 'kanban_column_created':
          if (message.data?.cartId === sessionId) {
            setColumns(prev => [...prev, message.data.column].sort((a, b) => a.position - b.position));
          }
          break;

        case 'kanban_column_updated':
          if (message.data?.cartId === sessionId) {
            setColumns(prev =>
              prev.map(col => col.id === message.data.column.id ? { ...col, ...message.data.column } : col)
                .sort((a, b) => a.position - b.position)
            );
          }
          break;

        case 'kanban_column_deleted':
          if (message.data?.cartId === sessionId) {
            setColumns(prev => prev.filter(col => col.id !== message.data.columnId));
          }
          break;

        case 'kanban_item_moved':
        case 'kanban_item_position_updated':
        case 'kanban_item_removed':
          if (message.data?.cartId === sessionId) {
            // Refresh board to get updated item positions
            refreshBoard();
          }
          break;
      }
    });

    return unsubscribe;
  }, [addMessageListener, sessionId, refreshBoard]);

  return {
    board,
    columns,
    loading,
    error,
    refreshBoard,
    createColumn,
    updateColumn,
    deleteColumn,
    moveItem,
    updateItemPosition,
    removeItemFromBoard,
  };
}